import { useEffect, useRef, useState } from "react";
import {
  Heart,
  CircleUser,
  ShoppingCart,
  User,
  LogOut,
  ChevronRight,
  ChevronLeft,
  Search,
} from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import WishlistModal from "../Wishlist/WishlistModal";
import LoginPromptModal from "../LoginModal/LoginPromptModal";
import axios from "axios";
import "../../assets/styles/components/Navbars/TopAnnouncementBar.css";
import API from "../../app/api";
import { useCartWishlist, useHeader } from "../../app/CartWishlistContext";
import { useDispatch } from "react-redux";
import searchicon from "../../assets/icons/Searchicon.svg";
import { getProductPdpUrl } from "../../utils/url.utils";

const TopAnnouncementBar = () => {
  const dispatch = useDispatch();
  const { countData } = useCartWishlist();
  const { showSearchIcon } = useHeader();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showUserPopup, setShowUserPopup] = useState(false);
  const [showWishlist, setShowWishlist] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [banners, setBanners] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [slideDirection, setSlideDirection] = useState("right");
  const [showSearch, setShowSearch] = useState(false);
  const inputRef = useRef(null);
  const [query, setQuery] = useState("");
  const [searchData, setSearchData] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const userWrapperRef = useRef(null);
  const [showSearchOnScroll, setShowSearchOnScroll] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const visibleResults = Array.isArray(searchData)
    ? searchData.slice(0, 8)
    : [];

  useEffect(() => {
    if (activeIndex < 0) return;
    const el = document.querySelector(".active-item");
    if (el) {
      el.scrollIntoView({
        block: "nearest",
      });
    }
  }, [activeIndex]);


  //  Check login status
  useEffect(() => {
    const checkLogin = () => {
      const token = localStorage.getItem("token");
      setIsLoggedIn(!!token);
    };
    checkLogin();
    window.addEventListener("storage", checkLogin);
    return () => window.removeEventListener("storage", checkLogin);
  }, []);

  //  Close popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        userWrapperRef.current &&
        !userWrapperRef.current.contains(event.target)
      ) {
        setShowUserPopup(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  //  Fetch banner data
  useEffect(() => {

    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/banners/announce-bar`, {
        headers: { accept: "application/json" },
      })
      .then((response) => {
        if (response.data?.data) {
          setBanners(response.data.data);
        }
      })
      .catch((error) => console.error("Error fetching banner:", error));
  }, []);

  //  Auto slide every 6s
  useEffect(() => {
    if (banners.length > 1) {
      const interval = setInterval(() => {
        setSlideDirection("right");
        setCurrentIndex((prev) => (prev + 1) % banners.length);
      }, 6000);
      return () => clearInterval(interval);
    }
  }, [banners]);

  /* ================= SCROLL SEARCH ICON CONTROL ================= */

  useEffect(() => {

    const handleScroll = () => {

      const heroBanner = document.querySelector(".homepage");

      if (!heroBanner) return;

      const bannerHeight = heroBanner.offsetHeight;

      if (window.scrollY > bannerHeight) {
        setShowSearchOnScroll(true);
      } else {
        setShowSearchOnScroll(false);
      }

    };

    window.addEventListener("scroll", handleScroll);

    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);

  }, []);

  useEffect(() => {
    if (!query.trim()) {
      setSearchData([]);
      return;
    }
    setLoading(true);
    handleSearch(query);
  }, [query, dispatch]);

  const handleSearch = async (query) => {
    try {
      const res = await API.get(`search?q=${query}`);
      if (res.data.status === 200) {
        setLoading(false);
        setSearchData(res.data?.data?.products);
      }
    } catch (err) {
      console.log(err);
      setLoading(false);
    }
  };

  const handleNext = () => {
    setSlideDirection("right");
    setCurrentIndex((prev) => (prev + 1) % banners.length);
  };

  const handlePrev = () => {
    setSlideDirection("left");
    setCurrentIndex((prev) =>
      prev === 0 ? banners.length - 1 : prev - 1
    );
  };

  //  Show user menu
  const handleUserClick = (e) => {
    e.stopPropagation(); // prevent outside click event
    if (!isLoggedIn) {
      navigate("/login");
    } else {
      setShowUserPopup((prev) => !prev);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("userName");
    setIsLoggedIn(false);
    setShowUserPopup(false);
    window.dispatchEvent(new Event("storage"));
    navigate("/");
  };

  const handleWishlistClick = () => {
    if (!isLoggedIn) navigate("/login");
    else setShowWishlist(true);
  };

  const handleCartClick = (e) => {
    if (!localStorage.getItem("token")) {
      e.preventDefault();
      navigate("/login");
    }
  };

  const claersearch = () => {
    setShowSearch(false);
    setQuery("");
    setSearchData([]);
  };

  // ================= HIGHLIGHT FUNCTION =================
  const highlightMatch = (text, query) => {
    if (!query) return text;
    const regex = new RegExp(`(${query})`, "gi");
    const parts = text.split(regex);
    return parts.map((part, index) =>
      regex.test(part) ? (
        <span key={index} className="highlight-text">{part}</span>
      ) : (
        part
      )
    );
  };

  return (
    <>
      {/* ================= TOP ANNOUNCEMENT BAR ================= */}
      <div className="top-announcement-bar">
        <div className="box_domain">
          {banners.length > 1 && (
            <div className="arrow-btn" onClick={handlePrev}>
              <ChevronLeft strokeWidth={1} />
            </div>
          )}
          <div
            key={currentIndex}
            className={`announce-text slide-${slideDirection}`}
          >
            {banners.length > 0 ? (
              <Link
                to={`/shop${banners[currentIndex]?.action_url}`}
                rel="noopener noreferrer"
              >
                {banners[currentIndex]?.title}
              </Link>
            ) : (
              ""
            )}
          </div>
          {banners.length > 1 && (
            <div className="arrow-btn" onClick={handleNext}>
              <ChevronRight strokeWidth={1} />
            </div>
          )}
        </div>

        <div className="icons-ann">
          {showSearchIcon && showSearchOnScroll && (
            <img
              src={searchicon}
              alt="search"
              className="search-icon-ann"
              style={{ cursor: "pointer" }}
              onClick={() => setShowSearch(true)}
            />
          )}
          <div
            ref={userWrapperRef}
            className="user-click-wrapper"
            style={{ position: "relative", left: "7px", top: "0px" }}
            onClick={handleUserClick}
          >
            <span style={{ fontSize: "12px", paddingRight: "7px" }}>
              {(localStorage.getItem("userName") ?? "").split(" ")[0]}
            </span>
            <span title="User Profile">
              <CircleUser
                color="#FFFFFF"
                size={26}
                strokeWidth={1}
                style={{ cursor: "pointer" }}
              />
            </span>

            {isLoggedIn && showUserPopup && (
              <div
                className="user-popup_AN_al"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="popup-triangle_an"></div>
                <div className="popup-item">
                  <Link to="/accounts/profile">
                    <User size={26} style={{ marginRight: 8 }} />
                    <span title="profile">Profile</span>
                  </Link>
                </div>
                <div className="popup-item" onClick={handleLogout}>
                  <LogOut size={26} style={{ marginRight: 8 }} />
                  <span title="logout">Logout</span>
                </div>
              </div>
            )}
          </div>

          <div
            title="Wish List"
            onClick={handleWishlistClick}
            style={{ cursor: "pointer", position: "relative" }}
          >
            <Heart size={26} strokeWidth={1} title="Wishlist" />
            <span className="wishlist-badge-home" style={{ background: "#FFF" }}>
              <span style={{ position: "relative", top: "0px", color: "#D52326" }}>
                {countData?.wishlist_count ?? ""}
              </span>
            </span>
          </div>

          <div title="Cart" style={{ position: "relative" }}>
            <Link to="/cart" onClick={handleCartClick} style={{ position: "relative" }}>
              <ShoppingCart
                size={26}
                style={{ cursor: "pointer" }}
                strokeWidth={1}
                title="Cart"
              />
              <span className="cart-badge" style={{ left: "85%", top: "0" }}>
                <span style={{ position: "relative", top: "1px" }}>
                  {countData?.cart_count ?? ""}
                </span>
              </span>
            </Link>
          </div>
        </div>
      </div>

      {/* ================= FULLSCREEN SEARCH MODAL ================= */}
      {showSearch && (
        <div className="search-overlay" onClick={claersearch}>
          <div className="search-modal-other" onClick={(e) => e.stopPropagation()}>
            <div className="d-flex position-relative">
              <input
                ref={inputRef}
                className="form-control border-0 input_home"
                placeholder="WHAT ARE YOU LOOKING FOR?"
                value={query}
                autoFocus
                onChange={(e) =>
                  setQuery(e.target.value.replace(/[^a-zA-Z0-9 ]/g, "").replace(/^\s+/, ""))
                }
                onKeyDown={(e) => {
    if (!visibleResults.length) return;

    //DOWN
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) =>
        prev < visibleResults.length - 1 ? prev + 1 : 0
      );
    }

    // UP
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) =>
        prev > 0 ? prev - 1 : visibleResults.length - 1
      );
    }

    // ⏎ ENTER
    if (e.key === "Enter") {
      e.preventDefault();

      if (activeIndex >= 0 && visibleResults[activeIndex]) {
        const item = visibleResults[activeIndex];
        claersearch();
        navigate(getProductPdpUrl(item.action_url));
      } else if (query.trim()) {
        claersearch();
        navigate(`/search?q=${encodeURIComponent(query.trim())}`);
      }
    }
  }}
              />

              <button
                type="button"
                className="btn btn-dark button_search"
                disabled={!query.trim()}
                onClick={() => {
                  claersearch();
                  navigate(`/search?q=${encodeURIComponent(query.trim())}`);
                }}
              >
                <Search strokeWidth={1.25} />
              </button>

              {/* ================= AUTOCOMPLETE ================= */}
             {query && (
                  <div className="autocomplete-box">
                    {loading && <div className="p-3 text-center">Searching...</div>}

                    {!loading && visibleResults.length === 0 && (
                      <div className="p-3 text-center">No results found</div>
                    )}

                    {!loading &&
                      visibleResults.map((item, i) => (
                        <Link
                          key={item.id}
                          to={getProductPdpUrl(item.action_url)}
                          className={`autocomplete-item ${
                            i === activeIndex ? "active-item" : ""
                          }`}
                          onMouseEnter={() => setActiveIndex(i)} 
                          onClick={claersearch}
                        >
                          <img
                            src={item.media_list?.main?.file}
                            alt={item.name}
                            className="autocomplete-img"
                          />

                          <span className="autocomplete-text">
                            {highlightMatch(item.name, query)}
                          </span>
                        </Link>
                      ))}
                  </div>
                )}
            </div>
          </div>
        </div>
      )}

      {showWishlist && <WishlistModal onClose={() => setShowWishlist(false)} />}
      {showLoginPrompt && (
        <LoginPromptModal onClose={() => setShowLoginPrompt(false)} />
      )}
    </>
  );
};

export default TopAnnouncementBar;